import { QueryClientProvider } from '@tanstack/react-query'
import 'bootstrap/dist/css/bootstrap.min.css'
import { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider } from './context/authentication/auth-context'
import { queryClient } from './services/queryClient'

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import 'chart.js/auto'
import { PreferencesProvider } from 'context/preferences/PreferencesContext'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { Z_INDEX } from 'styles/z-index'
import LoadingScreen from './components/Layout/LoadingScreen'
import { useAuth } from './context/authentication/useAuth'
import './main.css'
import apiService from './services/api'
import IntercomMessenger from './components/IntercomMessenger'
import Desktop from 'desktop'
import Mobile from 'mobile'

import { useUserAgent } from 'hooks/useUserAgent'
import { featureFlagService } from 'utils/featureFlagService'
import { publicFeatureFlags } from 'utils/publicFeatureFlags'
import {
  BulkDownloadProvider,
  BulkDownloadWidget,
} from 'features/bulk-downloads'

function Root() {
  const api = apiService()
  const { isAuthenticated, isCheckingAuth } = useAuth()
  const { isMobile } = useUserAgent()
  const authenticatedFlags = featureFlagService()

  const [enableMobileAppPublic, setEnableMobileAppPublic] = useState(false)
  const [publicFlagsLoaded, setPublicFlagsLoaded] = useState(false)

  // Load public feature flags when not authenticated
  useEffect(() => {
    if (!isAuthenticated && !isCheckingAuth) {
      publicFeatureFlags.loadFlags().then(() => {
        setEnableMobileAppPublic(
          publicFeatureFlags.isEnabled('enable_mobile_app')
        )
        setPublicFlagsLoaded(true)
      })
    }
  }, [isAuthenticated, isCheckingAuth])

  const enableMobileApp = isAuthenticated
    ? authenticatedFlags.enableMobileApp
    : enableMobileAppPublic
  const isLoadingFeatureFlags = isAuthenticated
    ? authenticatedFlags.isLoading
    : !publicFlagsLoaded

  // Common data shared across the app
  // We load it here so its available faster
  api.useGetActiveCompanyCategoriesAndTypes(!isAuthenticated)
  api.useGetUsers(!isAuthenticated)
  api.useGetSalesStages(!isAuthenticated)
  api.useGetTags('products', !isAuthenticated)
  api.useGetTags('contacts', !isAuthenticated)
  api.useGetProducts(!isAuthenticated)
  api.useGetCampaigns(undefined, !isAuthenticated)

  useEffect(() => {
    // If this is a production build, load the ga4 script
    if (process.env.NODE_ENV === 'production') {
      const src_script = document.createElement('script')
      src_script.src =
        'https://www.googletagmanager.com/gtag/js?id=G-2DXQWDV89Z'
      src_script.async = true

      const script = document.createElement('script')
      script.innerHTML =
        " window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-2DXQWDV89Z'); "

      document.head.appendChild(src_script)
      document.head.appendChild(script)

      return () => {
        document.head.removeChild(src_script)
        document.head.removeChild(script)
      }
    }
  }, [])

  if (isCheckingAuth || isLoadingFeatureFlags) {
    return <LoadingScreen />
  }

  return (
    <>
      {isMobile && (enableMobileApp || !isAuthenticated) ? (
        <Mobile />
      ) : (
        <Desktop />
      )}
      <IntercomMessenger />
    </>
  )
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <AuthProvider>
    <QueryClientProvider client={queryClient}>
      <PreferencesProvider>
        <BulkDownloadProvider>
          <Root />
          <BulkDownloadWidget />
          <ReactQueryDevtools />
          <ToastContainer style={{ zIndex: Z_INDEX.NOTIFICATION }} />
        </BulkDownloadProvider>
      </PreferencesProvider>
    </QueryClientProvider>
  </AuthProvider>
)
